import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { canInteract } from '../PropertySelector.utils';

import type {
  PropertySelectionContextType,
  PropertySelectionProviderProps
} from './PropertySelectionContext.types';

const PropertySelectionContext = createContext<PropertySelectionContextType | null>(null);

export const PropertySelectionProvider = ({
  children,
  properties
}: PropertySelectionProviderProps) => {
  const [pendingActivePropertyRefs, setPendingActivePropertyRefs] = useState<Set<string>>(() => {
    return new Set(properties.filter((p) => p.active).map((p) => p.ref));
  });

  const [prevProperties, setPrevProperties] = useState(properties);
  if (properties !== prevProperties) {
    setPrevProperties(properties);
    setPendingActivePropertyRefs(new Set(properties.filter((p) => p.active).map((p) => p.ref)));
  }

  const [alertMessage, setAlertMessage] =
    useState<PropertySelectionContextType['alertMessage']>(null);

  const toggleProperty = useCallback((ref: string, isActive: boolean) => {
    setPendingActivePropertyRefs((prevRefs) => {
      const updatedRefs = new Set(prevRefs);
      if (isActive) {
        updatedRefs.add(ref);
      } else {
        updatedRefs.delete(ref);
      }
      return updatedRefs;
    });
  }, []);

  const applyChanges = useCallback(() => {
    return properties.map((prop) => ({
      ...prop,
      active: pendingActivePropertyRefs.has(prop.ref)
    }));
  }, [properties, pendingActivePropertyRefs]);

  const selectAll = useCallback(() => {
    setPendingActivePropertyRefs(new Set(properties.filter(canInteract).map((p) => p.ref)));
  }, [properties]);

  const deselectAll = useCallback(() => {
    setPendingActivePropertyRefs(new Set());
  }, []);

  const resetPendingChanges = useCallback(() => {
    setPendingActivePropertyRefs(new Set(properties.filter((p) => p.active).map((p) => p.ref)));
  }, [properties]);

  const areAllSelected = useMemo(() => {
    const interactableRefs = properties.filter(canInteract).map((p) => p.ref);
    return (
      interactableRefs.length > 0 &&
      interactableRefs.every((ref) => pendingActivePropertyRefs.has(ref))
    );
  }, [properties, pendingActivePropertyRefs]);

  const value = useMemo(
    () => ({
      pendingActivePropertyRefs,
      toggleProperty,
      selectAll,
      deselectAll,
      areAllSelected,
      applyChanges,
      resetPendingChanges,
      alertMessage,
      setAlertMessage
    }),
    [
      pendingActivePropertyRefs,
      toggleProperty,
      selectAll,
      deselectAll,
      areAllSelected,
      applyChanges,
      resetPendingChanges,
      alertMessage
    ]
  );

  return (
    <PropertySelectionContext.Provider value={value}>{children}</PropertySelectionContext.Provider>
  );
};

export const usePropertySelection = () => {
  const context = useContext(PropertySelectionContext);
  if (!context) {
    throw new Error('usePropertySelection must be used within a PropertySelectionProvider');
  }
  return context;
};
